import { getAxiosInstance } from './axiosInstance';
import { getScimConfig } from '../utils/envUtils';
import { AxiosError } from 'axios';

export interface MeData {
    [key: string]: any;
    ApiRole?: string;
    DisplayName?: string;
    Id?: string;
    EntityType?: string;
}

export interface MeFetchError {
    status: number | null;
    message: string;
    isAdminOnAdmin: boolean;
}

class MeService {
    private meData: MeData | null = null;
    private loading: boolean = false;
    private error: string | null = null;
    private lastFetchError: MeFetchError | null = null;
    private listeners: Set<() => void> = new Set();

    /**
     * Fetch the current user data from entities/me endpoint
     */
    async fetchMe(): Promise<MeData | null> {
        if (this.loading) {
            return new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (!this.loading) {
                        clearInterval(checkInterval);
                        resolve(this.meData);
                    }
                }, 100);
            });
        }

        this.loading = true;
        this.error = null;
        this.lastFetchError = null;

        try {
            const api = getAxiosInstance(getScimConfig().apiUrl);
            const response = await api.get<MeData>('/entities/me');
            const data = response.data;
            this.meData = data;
            this.notifyListeners();
            return data;
        } catch (error) {
            console.error('Error fetching entities/me:', error);
            this.error = error instanceof Error ? error.message : 'Unknown error occurred';
            this.lastFetchError = MeService.extractFetchError(error);
            this.meData = null;
            this.notifyListeners();
            throw error;
        } finally {
            this.loading = false;
        }
    }

    private static extractFetchError(error: unknown): MeFetchError {
        const axiosErr = error as AxiosError;
        const status = axiosErr?.response?.status ?? null;
        const responseData = axiosErr?.response?.data as any;
        const detail = responseData?.detail ?? responseData?.message ?? responseData?.Detail ?? '';
        const scimDetail = responseData?.scimType ?? '';

        const isAdminOnAdmin = status === 403 && (
            /admin/i.test(detail) ||
            /impersonat/i.test(detail) ||
            /on.behalf/i.test(detail) ||
            scimDetail === 'adminImpersonation'
        );

        let message: string;
        if (isAdminOnAdmin) {
            message = 'You cannot impersonate another administrator.';
        } else if (status === 403) {
            message = detail || 'Access denied. You do not have permission to impersonate this user.';
        } else if (status === 404) {
            message = 'User not found. Please verify the UPN is correct.';
        } else if (status != null && status >= 400) {
            message = detail || `Request failed (HTTP ${status}).`;
        } else {
            message = error instanceof Error ? error.message : 'An unknown error occurred.';
        }

        return { status, message, isAdminOnAdmin };
    }

    getLastFetchError(): MeFetchError | null {
        return this.lastFetchError;
    }

    /**
     * Get cached Me data (returns null if not yet fetched)
     */
    getMe(): MeData | null {
        return this.meData;
    }

    /**
     * Get ApiRole from cached Me data
     */
    getApiRole(): string | null {
        return this.meData?.ApiRole || null;
    }

    /**
     * Check if Me data is currently loading
     */
    isLoading(): boolean {
        return this.loading;
    }

    /**
     * Get the last error, if any
     */
    getError(): string | null {
        return this.error;
    }

    /**
     * Clear cached Me data
     */
    clearMe(): void {
        this.meData = null;
        this.error = null;
        this.notifyListeners();
    }

    /**
     * Subscribe to changes in Me data
     */
    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    private notifyListeners(): void {
        this.listeners.forEach(listener => listener());
    }
}

export default new MeService();

